import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useReferrals } from "@/hooks/useReferrals";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { Link as LinkIcon } from "lucide-react";
import { ReferralLinkDialog } from "@/components/referral/ReferralLinkDialog";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  name: z.string().optional(),
  companyName: z.string().optional(),
  comment: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteSuccess?: () => void;
}

export function InviteUserDialog({
  open,
  onOpenChange,
  onInviteSuccess,
}: InviteUserDialogProps) {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { useGenerateReferralMutation } = useReferrals();
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [generatedReferralCode, setGeneratedReferralCode] = useState("");
  const generateReferral = useGenerateReferralMutation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      name: "",
      companyName: "",
      comment: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    if (!user) {
      toast.error("You must be logged in to invite users");
      return;
    }

    setIsSubmitting(true);
    try {
      const token = uuidv4();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);
      
      const payload = {
        email: data.email,
        name: data.name || null,
        company_name: data.companyName || null,
        comment: data.comment || null,
        roles: ["USER"],
        token: token,
        invited_by: user.id,
        expires_at: expiresAt.toISOString(),
      } as const;
      
      const { error: dbError } = await supabase
        .from("user_invitations")
        .insert(payload as any);

      if (dbError) {
        if (dbError.code === "23505") {
          toast.error("This email has already been invited and is pending");
        } else {
          toast.error(`Error creating invitation: ${dbError.message}`);
        }
        return;
      }

      const { error: emailError } = await supabase.functions.invoke(
        "send-invitation-email",
        {
          body: {
            email: data.email,
            name: data.name,
            companyName: data.companyName,
            comment: data.comment,
            roles: ["USER"],
            token: token,
            invitedBy: {
              name: user.name,
              email: user.email,
            },
          },
        }
      );

      if (emailError) {
        toast.error(`Error sending invitation email: ${emailError.message}`);
        return;
      }

      toast.success(`Invitation sent to ${data.email}`);
      form.reset();
      onInviteSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(`Error: ${error.message || "Something went wrong"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateReferralLink = async () => {
    if (!user) {
      toast.error("You must be logged in to generate referral links");
      return;
    }

    try {
      const code = await generateReferral.mutateAsync(user.id);
      setGeneratedReferralCode(code);
      setShowReferralDialog(true);
    } catch (error) {
      console.error("Error generating referral link:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite a new user</DialogTitle>
          <DialogDescription>
            Send an invitation email to add a new user to our platform.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input placeholder="user@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Company Inc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Personal Message (optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Add a personal message to include with the invitation..."
                      className="min-h-[80px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    This message will be included in the invitation email.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> Invited users will be assigned the "User" role automatically.
              </p>
            </div>

            <DialogFooter>
              <div className="flex w-full justify-between items-center">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={handleGenerateReferralLink}
                  disabled={generateReferral.isPending}
                >
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Generate Invitation Link
                </Button>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Sending Invitation..." : "Send Invitation"}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
      
      <ReferralLinkDialog
        open={showReferralDialog}
        onOpenChange={setShowReferralDialog}
        referralCode={generatedReferralCode}
        userName={user?.name}
      />
    </Dialog>
  );
}